// src/features/invoice/utils/invoiceHelpers.js
import {
  INVOICE_STATUS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_ICONS,
  INVOICE_STATUS_TRANSITIONS,
  DISCOUNT_TYPES,
  CURRENCY_OPTIONS,
} from "../types/invoiceTypes";

// Calculate totals - pure function, no recursion
export const calculateTotals = (
  items,
  discount = 0,
  discountType = DISCOUNT_TYPES.PERCENTAGE,
  taxRate = 0,
) => {
  let subtotal = 0;
  if (items && Array.isArray(items)) {
    items.forEach((item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      subtotal += quantity * unitPrice;
    });
  }

  let discountAmount = 0;
  if (discountType === DISCOUNT_TYPES.PERCENTAGE) {
    discountAmount = subtotal * (discount / 100);
  } else {
    discountAmount = discount || 0;
  }

  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * ((taxRate || 0) / 100);
  const total = subtotal - discountAmount + taxAmount;

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discountAmount: Math.round(discountAmount * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    total: Math.round(total * 100) / 100,
  };
};

// Generate invoice number
export const generateInvoiceNumber = (sequence, prefix = "INV") => {
  return `${prefix}-${String(sequence).padStart(4, "0")}`;
};

// ✅ Add this helper function
export const formatINR = (amount) => {
  if (amount === undefined || amount === null) return "₹0.00";
  const num = Number(amount);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

// ✅ Update formatCurrency to default to INR
export const formatCurrency = (amount, currency = "INR") => {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};
// Format Indian Rupee without currency symbol (for input fields)
export const formatIndianNumber = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);
};

// Format Indian Rupee with abbreviation (K, L, Cr)
export const formatIndianCurrencyAbbreviated = (amount) => {
  const num = Number(amount) || 0;

  if (num >= 10000000) {
    // 1 Crore
    return `₹${(num / 10000000).toFixed(1)} Cr`;
  }
  if (num >= 100000) {
    // 1 Lakh
    return `₹${(num / 100000).toFixed(1)} L`;
  }
  if (num >= 1000) {
    return `₹${(num / 1000).toFixed(1)} K`;
  }
  return formatCurrency(num);
};

// Check if invoice is overdue
export const isInvoiceOverdue = (dueDate) => {
  return new Date(dueDate) < new Date();
};

// Get days overdue
export const getOverdueDays = (dueDate) => {
  const diffTime = new Date() - new Date(dueDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Get status label
export const getStatusLabel = (status) => {
  return INVOICE_STATUS_LABELS[status] || status;
};

// Get status color
export const getStatusColor = (status) => {
  return INVOICE_STATUS_COLORS[status] || "#d9d9d9";
};

// Get status icon
export const getStatusIcon = (status) => {
  return INVOICE_STATUS_ICONS[status] || "📄";
};

// Check if status transition is valid
export const isValidStatusTransition = (currentStatus, newStatus) => {
  const allowedTransitions = INVOICE_STATUS_TRANSITIONS[currentStatus] || [];
  return allowedTransitions.includes(newStatus);
};

// Filter invoices
export const filterInvoices = (invoices, filters = {}) => {
  let result = [...invoices];

  if (filters.status && filters.status !== "all") {
    result = result.filter((inv) => inv.status === filters.status);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    result = result.filter(
      (inv) =>
        inv.number.toLowerCase().includes(searchLower) ||
        inv.clientName.toLowerCase().includes(searchLower) ||
        (inv.title && inv.title.toLowerCase().includes(searchLower)),
    );
  }

  if (filters.dateRange?.start) {
    const startDate = new Date(filters.dateRange.start);
    result = result.filter((inv) => new Date(inv.date) >= startDate);
  }
  if (filters.dateRange?.end) {
    const endDate = new Date(filters.dateRange.end);
    result = result.filter((inv) => new Date(inv.date) <= endDate);
  }

  if (filters.clientId && filters.clientId !== "all") {
    result = result.filter((inv) => inv.clientId === filters.clientId);
  }

  if (filters.minAmount) {
    result = result.filter((inv) => inv.total >= filters.minAmount);
  }
  if (filters.maxAmount) {
    result = result.filter((inv) => inv.total <= filters.maxAmount);
  }

  if (filters.paymentMethod && filters.paymentMethod !== "all") {
    result = result.filter(
      (inv) => inv.paymentMethod === filters.paymentMethod,
    );
  }

  return result;
};

// Sort invoices
export const sortInvoices = (invoices, sortBy = "date", sortOrder = "desc") => {
  const sorted = [...invoices];

  sorted.sort((a, b) => {
    let valueA = a[sortBy];
    let valueB = b[sortBy];

    if (
      sortBy === "date" ||
      sortBy === "createdAt" ||
      sortBy === "dueDate" ||
      sortBy === "paymentDate"
    ) {
      valueA = new Date(valueA).getTime();
      valueB = new Date(valueB).getTime();
    } else if (sortBy === "total") {
      valueA = parseFloat(valueA) || 0;
      valueB = parseFloat(valueB) || 0;
    } else if (sortBy === "clientName") {
      valueA = (valueA || "").toLowerCase();
      valueB = (valueB || "").toLowerCase();
    } else if (sortBy === "status") {
      valueA = INVOICE_STATUS_LABELS[valueA] || valueA;
      valueB = INVOICE_STATUS_LABELS[valueB] || valueB;
    }

    if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
    if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
};

// Calculate invoice statistics
export const calculateInvoiceStats = (invoices) => {
  const total = invoices.length;

  const statusCounts = {
    draft: 0,
    sent: 0,
    viewed: 0,
    paid: 0,
    partially_paid: 0,
    overdue: 0,
    cancelled: 0,
    refunded: 0,
  };

  let totalValue = 0;
  let totalPaid = 0;
  let overdueCount = 0;

  invoices.forEach((inv) => {
    if (statusCounts[inv.status] !== undefined) {
      statusCounts[inv.status]++;
    }
    totalValue += inv.total || 0;
    totalPaid += inv.amountPaid || 0;
    if (inv.isOverdue || inv.status === "overdue") {
      overdueCount++;
    }
  });

  const collectionRate = total > 0 ? (totalPaid / totalValue) * 100 : 0;
  const averageValue = total > 0 ? totalValue / total : 0;

  return {
    total,
    ...statusCounts,
    totalValue: Math.round(totalValue * 100) / 100,
    totalPaid: Math.round(totalPaid * 100) / 100,
    totalOutstanding: Math.round((totalValue - totalPaid) * 100) / 100,
    averageValue: Math.round(averageValue * 100) / 100,
    collectionRate: Math.round(collectionRate * 100) / 100,
    overdueCount: overdueCount,
  };
};

// Convert number to Indian words (for cheque/invoice amounts)
export const numberToIndianWords = (num) => {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  if (num === 0) return "Zero";

  const convertHundreds = (n) => {
    if (n === 0) return "";
    const hundred = Math.floor(n / 100);
    const remainder = n % 100;
    let result = "";

    if (hundred > 0) {
      result += ones[hundred] + " Hundred ";
    }

    if (remainder > 0) {
      if (remainder < 10) {
        result += ones[remainder];
      } else if (remainder < 20) {
        result += teens[remainder - 10];
      } else {
        const ten = Math.floor(remainder / 10);
        const one = remainder % 10;
        result += tens[ten];
        if (one > 0) {
          result += " " + ones[one];
        }
      }
    }
    return result.trim();
  };

  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const hundred = Math.floor(num % 1000);

  let result = "";

  if (crore > 0) {
    result += convertHundreds(crore) + " Crore ";
  }
  if (lakh > 0) {
    result += convertHundreds(lakh) + " Lakh ";
  }
  if (thousand > 0) {
    result += convertHundreds(thousand) + " Thousand ";
  }
  if (hundred > 0) {
    result += convertHundreds(hundred);
  }

  return result.trim();
};
